import { useAuth0 } from '@auth0/auth0-react';
import { ArrowUpTrayIcon, PlusIcon } from '@heroicons/react/24/outline';
import React, { useState, useEffect, useCallback } from 'react';

import { getUploadUrl, uploadFileToS3 } from '../../../apis/fileApi';
import { UploadedReceipt } from '../../../types';
import { BOOKING_REVIEW_STATUSES } from '../apis/types';
import { createUploadedReceipt } from '../apis/uploadedReceiptApi';
import { useGenerateAllBookings } from '../hooks/useGenerateAllBookings';
import { useImportAllBookings } from '../hooks/useImportAllBookings';
import { useReceiptCollection } from '../hooks/useReceiptCollection';
import { useRetryAllExtractions } from '../hooks/useRetryAllExtractions';

import CollapsibleBookingCard from './CollapsibleBookingCard';
import FileDragDropZone from './FileDragDropZone';
import ImportFailedBanner from './ImportFailedBanner';
import ProcessAllButton from './ProcessAllButton';
import ReceiptItem from './ReceiptItem';
import UploadingReceiptItem from './UploadingReceiptItem';

export interface UploadingFile {
  id: string;
  file: File;
  fileName: string;
  progress: number;
  status: 'uploading' | 'processing' | 'error';
  error?: string;
}

interface BulkOperationDetailsReceiptsProps {
  bulkOperationId: string;
  receipts: UploadedReceipt[];
  receiptsLoading: boolean;
  onRemoveReceipt: (receiptId: string) => void;
  removingReceiptIds: Set<string>;
  onUploadingStateChange?: (hasUploading: boolean) => void;
  hasUploadingFiles: boolean;
}

const BulkOperationDetailsReceipts: React.FC<
  BulkOperationDetailsReceiptsProps
> = ({
  bulkOperationId,
  receipts,
  receiptsLoading,
  onRemoveReceipt,
  removingReceiptIds,
  onUploadingStateChange,
  hasUploadingFiles,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);

  const { addReceipts } = useReceiptCollection(bulkOperationId);
  const { generateAllBookings, isGeneratingAll } =
    useGenerateAllBookings(bulkOperationId);
  const { importAllBookings, isImportingAll } =
    useImportAllBookings(bulkOperationId);
  const { retryAllExtractions, isRetryingAll } =
    useRetryAllExtractions(bulkOperationId);

  // Sync uploading state with parent component
  useEffect(() => {
    onUploadingStateChange?.(uploadingFiles.length > 0);
  }, [uploadingFiles.length, onUploadingStateChange]);

  const extractionFailedCount = receipts.filter(
    r => r.status === 'text-extraction-failed'
  ).length;
  const generatableCount = receipts.filter(
    r => r.status === 'ready' || r.status === 'generation-failed'
  ).length;
  const importableCount = receipts.filter(r => r.status === 'generated').length;

  const isProcessing = isGeneratingAll || isImportingAll || isRetryingAll;

  const handleProcessAll = useCallback(() => {
    const promises: Promise<unknown>[] = [];

    if (extractionFailedCount > 0) {
      const failedIds = receipts
        .filter(r => r.status === 'text-extraction-failed')
        .map(r => r.id);
      promises.push(retryAllExtractions(failedIds));
    }
    if (generatableCount > 0) {
      promises.push(generateAllBookings());
    }
    if (importableCount > 0) {
      promises.push(importAllBookings());
    }

    Promise.allSettled(promises);
  }, [
    extractionFailedCount,
    generatableCount,
    importableCount,
    receipts,
    retryAllExtractions,
    generateAllBookings,
    importAllBookings,
  ]);

  const totalCount = receipts.length + uploadingFiles.length;
  const isEmpty = !receiptsLoading && receipts.length === 0 && uploadingFiles.length === 0;

  const handleFiles = async (fileList: FileList) => {
    const token = await getAccessTokenSilently();
    const filesArray = Array.from(fileList);

    const validFiles: UploadingFile[] = [];
    for (const file of filesArray) {
      if (file.type !== 'application/pdf') {
        continue;
      }
      validFiles.push({
        id: `${Date.now()}-${Math.random()}`,
        file,
        fileName: file.name,
        progress: 0,
        status: 'uploading',
      });
    }

    if (validFiles.length === 0) return;

    setUploadingFiles(prev => [...prev, ...validFiles]);

    const newReceipts: UploadedReceipt[] = [];
    const successfulUploadIds: string[] = [];

    const uploadPromises = validFiles.map(async uploadingFile => {
      try {
        // Phase 1: Upload to S3
        const { url, key } = await getUploadUrl(
          {
            fileName: uploadingFile.file.name,
            contentType: uploadingFile.file.type,
            folder: 'receipts',
          },
          token
        );

        await uploadFileToS3(url, uploadingFile.file, progress => {
          setUploadingFiles(prev =>
            prev.map(f => (f.id === uploadingFile.id ? { ...f, progress } : f))
          );
        });

        // Phase 2: Create uploaded receipt record
        setUploadingFiles(prev =>
          prev.map(f =>
            f.id === uploadingFile.id
              ? { ...f, status: 'processing' as const, progress: 100 }
              : f
          )
        );

        const receipt = await createUploadedReceipt(
          {
            storageKey: key,
            contentType: uploadingFile.file.type,
            originalFileName: uploadingFile.file.name,
            fileSizeInBytes: uploadingFile.file.size,
          },
          token
        );

        newReceipts.push(receipt);
        successfulUploadIds.push(uploadingFile.id);
      } catch (err) {
        console.error('Failed to upload file:', err);
        setUploadingFiles(prev =>
          prev.map(f =>
            f.id === uploadingFile.id
              ? {
                  ...f,
                  status: 'error' as const,
                  error: err instanceof Error ? err.message : 'Upload failed',
                }
              : f
          )
        );
      }
    });

    await Promise.all(uploadPromises);

    // Phase 3: Add receipt IDs to bulk operation
    if (newReceipts.length > 0) {
      try {
        await addReceipts(
          newReceipts.map(r => r.id),
          newReceipts
        );

        // Success: receipts are now in the cache, remove uploading items
        setUploadingFiles(prev =>
          prev.filter(f => !successfulUploadIds.includes(f.id))
        );
      } catch (err) {
        console.error('Failed to add receipts to bulk operation:', err);
        // Show error on uploading items so the user knows what happened
        setUploadingFiles(prev =>
          prev.map(f =>
            successfulUploadIds.includes(f.id)
              ? {
                  ...f,
                  status: 'error' as const,
                  error: 'Failed to add to import',
                }
              : f
          )
        );
      }
    }
  };

  const handleRemoveUploadingFile = (fileId: string) => {
    setUploadingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const renderReceipt = (receipt: UploadedReceipt) => {
    // Receipts in booking review statuses with booking data get a collapsible card
    if (BOOKING_REVIEW_STATUSES.includes(receipt.status)) {
      const hasBookingData =
        !!receipt.lexOfficeBooking ||
        (Array.isArray(receipt.datevBooking) &&
          receipt.datevBooking.length > 0);

      if (hasBookingData) {
        return (
          <CollapsibleBookingCard
            key={receipt.id}
            receipt={receipt}
            isDisabled={false}
            onRemove={onRemoveReceipt}
            isRemoving={removingReceiptIds.has(receipt.id)}
          />
        );
      }

      // Import-failed without booking data
      if (receipt.status === 'import-failed') {
        return (
          <div key={receipt.id}>
            <ImportFailedBanner errorMessage={receipt.errorMessage} />
          </div>
        );
      }
    }

    // All other receipts get a simple receipt item
    return (
      <ReceiptItem
        key={receipt.id}
        receipt={receipt}
        onRemove={onRemoveReceipt}
        isRemoving={removingReceiptIds.has(receipt.id)}
        isGeneratingAll={isGeneratingAll}
        isRetryingAll={isRetryingAll}
      />
    );
  };

  return (
    <FileDragDropZone
      onFilesAdded={handleFiles}
      footer={
        <div className='flex items-center justify-center flex-col'>
          <span className='text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-1 text-sm transition-colors duration-200'>
            <PlusIcon className='w-4 h-4' />
            <span>Click here or drop files to add receipts</span>
          </span>
        </div>
      }
    >
      {/* Section Header */}
      <div className='flex items-center justify-between mb-6'>
        <h2 className='text-xl font-semibold text-gray-900 dark:text-gray-100'>
          Uploaded Receipts ({totalCount})
        </h2>
        <ProcessAllButton
          onProcess={handleProcessAll}
          isProcessing={isProcessing}
          extractionFailedCount={extractionFailedCount}
          generatableCount={generatableCount}
          importableCount={importableCount}
          totalReceiptCount={receipts.length}
          hasUploadingFiles={hasUploadingFiles}
        />
      </div>

      {/* Loading State */}
      {receiptsLoading && receipts.length === 0 && (
        <div className='space-y-3'>
          {[1, 2, 3].map(i => (
            <div key={i} className='animate-pulse bg-gray-100 dark:bg-gray-800 rounded-lg h-16' />
          ))}
        </div>
      )}

      {/* Empty State */}
      {isEmpty && (
        <div className='text-center py-12'>
          <ArrowUpTrayIcon className='w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4' />
          <p className='text-gray-500 dark:text-gray-400 mb-2'>
            No receipts in this import yet
          </p>
          <p className='text-sm text-gray-400 dark:text-gray-500 mb-4'>
            Drag and drop PDF files here or click the button above
          </p>
        </div>
      )}

      {/* Receipt List */}
      {!isEmpty && (receipts.length > 0 || uploadingFiles.length > 0) && (
        <div className='space-y-3'>
          {receipts.map(renderReceipt)}

          {uploadingFiles.map(uploadingFile => (
            <UploadingReceiptItem
              key={uploadingFile.id}
              id={uploadingFile.id}
              fileName={uploadingFile.fileName}
              progress={uploadingFile.progress}
              status={uploadingFile.status}
              error={uploadingFile.error}
              onRemove={handleRemoveUploadingFile}
            />
          ))}
        </div>
      )}
    </FileDragDropZone>
  );
};

export default BulkOperationDetailsReceipts;
